import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FieldConfig } from '../field-interface';
import { UntypedFormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { AppDateAdapter, APP_DATE_FORMATS } from '../../directive/date';
import { Subject } from 'rxjs';

@Component({
  selector: 'calsoft-date-time',
  templateUrl: './date-time.component.html',
  styleUrls: ['./date-time.component.scss'],
  providers: [
    {provide: DateAdapter, useClass: AppDateAdapter},
    {provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS}
  ]
})
export class DateTimeComponent implements OnInit, OnDestroy {

  sanitizedUrl: string = '';
  url = '';

  field: FieldConfig['eavAttribute'];
  group: UntypedFormGroup;

  dateValue: any = null;
  timeValue: string = '';

  get isRequired(): boolean {
    if (!this.group || !this.field || !this.field.attributeCode) return false;
    const ctrl = this.group.get(this.field.attributeCode);
    if (!ctrl) return false;
    if (this.field.isRequired === 1) return true;
    return ctrl.errors?.required !== undefined;
  }

  get isInvalid(): boolean {
    if (!this.group || !this.field || !this.field.attributeCode) return false;
    const ctrl = this.group.get(this.field.attributeCode);
    return ctrl ? (ctrl.invalid && (ctrl.dirty || ctrl.touched)) : false;
  }

  ngOnInit() {
    this.initDateTimeFromControl();

    if (this.group && this.field?.attributeCode) {
      const ctrl = this.group.get(this.field.attributeCode);
      if (ctrl) {
        ctrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(val => {
          if (val !== this.getCombinedValue()) {
            this.initDateTimeFromControl();
          }
        });
      }
    }
  }

  initDateTimeFromControl() {
    if (!this.group || !this.field?.attributeCode) return;
    const rawVal = this.group.get(this.field.attributeCode)?.value;
    if (!rawVal) {
      this.dateValue = null;
      this.timeValue = '';
      return;
    }

    if (rawVal instanceof Date) {
      this.dateValue = rawVal;
      const hh = rawVal.getHours().toString().padStart(2, '0');
      const mm = rawVal.getMinutes().toString().padStart(2, '0');
      this.timeValue = `${hh}:${mm}`;
    } else if (typeof rawVal === 'string') {
      const str = rawVal.trim();
      if (str.includes('T') || str.includes(' ')) {
        const separator = str.includes('T') ? 'T' : ' ';
        const parts = str.split(separator);
        this.dateValue = new Date(str);
        if (isNaN(this.dateValue.getTime())) {
          this.dateValue = new Date(parts[0]);
        }
        if (parts[1]) {
          this.timeValue = parts[1].substring(0, 5);
        }
      } else {
        this.dateValue = new Date(str);
        this.timeValue = '';
      }
    }
  }

  onDateTimeChange() {
    if (!this.group || !this.field?.attributeCode) return;
    const combined = this.getCombinedValue();
    const ctrl = this.group.get(this.field.attributeCode);
    if (ctrl) {
      ctrl.setValue(combined);
      ctrl.markAsDirty();
      ctrl.markAsTouched();
    }
  }

  getCombinedValue(): string | null {
    if (!this.dateValue) return null;
    let d: Date;
    if (this.dateValue instanceof Date) {
      d = this.dateValue;
    } else {
      d = new Date(this.dateValue);
    }
    if (!d || isNaN(d.getTime())) return null;

    const yyyy = d.getFullYear();
    const MM = (d.getMonth() + 1).toString().padStart(2, '0');
    const dd = d.getDate().toString().padStart(2, '0');

    const time = this.timeValue ? this.timeValue.trim() : '00:00';
    const timeParts = time.split(':');
    const hh = (timeParts[0] || '00').padStart(2, '0');
    const mm = (timeParts[1] || '00').padStart(2, '0');
    const ss = timeParts[2] ? timeParts[2].padStart(2, '0') : '00';

    return `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}`;
  }

  sanitizeUrl(u: string): string {
    return (u || '')
      .replace(/^\/+/, '')
      .replace(/[?#&=]/g, '-')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  }

  protected _onDestroy = new Subject<void>();

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  constructor(private router: Router) {
    this.url = this.router.url || '';
    this.router.events
      .pipe(filter((e: any) => e instanceof NavigationEnd), takeUntil(this._onDestroy))
      .subscribe(() => { this.url = this.router.url || ''; });
  }
}
